import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Box, Button, Typography, Chip, Grid, Card, CardContent, Tooltip, Paper, TextField, FormControl, InputLabel, Select, Autocomplete, MenuItem, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, IconButton, TablePagination, Stack, Divider } from "@mui/material";
import { Search, Visibility, RateReview, PendingActions, FilterAlt, FilterAltOff, ArrowBack, RestartAlt } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from "react-router-dom";
import { getData } from "../../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../../services/endpoints";
import Loader from "../../../../common/spinner";
import { getStatusColor, priorityColors, issueTypeColors } from "../../../../utils/statusColor";
import { formatDate } from "../../../../utils/dateFormat";

function UserTasksPage({ epicData, projectData }) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userRole = userData?.user_type;
    const roleBasePath = userRole === 'Team Lead' ? '/team-lead' : '/manager';
    const projectId = useMemo(() => searchParams.get("projectId"), [searchParams]);
    const projectTitle = useMemo(() => searchParams.get("projectTitle"), [searchParams]);
    const epicId = useMemo(() => searchParams.get("epicId"), [searchParams]);
    const userStoryId = useMemo(() => searchParams.get("storyId"), [searchParams]);
    const origin = useMemo(() => searchParams.get("origin"), [searchParams]);
    const [loading, setLoading] = useState(true);
    const [taskData, setTaskData] = useState({ tasks: [], total: 0, stats: {} });
    const isFetching = useRef(false);

    const [state, setState] = useState({
        view: 'stories',
        search: '',
        type: 'All',
        originFilter: 'All',
        priority: 'All',
        status: 'All',
        timeline: 'All',
        startDate: null,
        endDate: null,
        page: 0,
        rowsPerPage: 10,
        taskId: null,
        showFilters: false,
    });

    const fetchStoryDetails = useCallback(async () => {
        if (isFetching.current) return;

        const userDataRaw = localStorage.getItem('userData');
        const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
        const userId = userData?.id;

        // Safety check: Don't fetch if IDs are missing
        if (!userId || !projectId) return;

        try {
            isFetching.current = true;
            setLoading(true);

            // Prepare params with project_id as mandatory
            const params = {
                project_id: projectId,
                user_id: userId,       // Mandatory for this specific view
                origin: origin || "",
                page: state.page + 1,
                limit: state.rowsPerPage,
            };

            // Conditionally add epic_id
            if (origin === "Epic" && epicId) {
                params.epic_id = epicId;
            }
            if (origin === "Story" && userStoryId) {
                params.user_story_id = userStoryId;
            }

            // Add optional filters
            if (state.status !== 'All') params.status = state.status;
            if (state.priority !== 'All') params.priority = state.priority;
            if (state.type !== 'All') params.work_type = state.type;
            if (state.search) params.search = state.search;

            const url = API_ENDPOINTS.fetchAllTasks(params);
            const response = await getData(url);

            if (response.status === "SUCCESS") {
                const { tasks, pagination, stats } = response.result;
                setTaskData({
                    tasks: Array.isArray(tasks) ? tasks : [],
                    total: pagination?.totalItems || 0,
                    stats: stats || {}
                });
            }
        } catch (err) {
            setTaskData({ tasks: [], total: 0, stats: {} });
        } finally {
            setLoading(false);
            isFetching.current = false;
        }
    }, [projectId, epicId, userStoryId, origin, state.status, state.priority, state.type, state.search, state.page, state.rowsPerPage]);

    useEffect(() => {
        fetchStoryDetails();
    }, [fetchStoryDetails]);

    const searchSuggestions = useMemo(() => {
        const titles = taskData.tasks.map(t => t.task_title);
        const projects = taskData.tasks.map(t => t.Project?.project_title).filter(Boolean);
        return Array.from(new Set([...titles, ...projects]));
    }, [taskData.tasks]);

    const statsCards = useMemo(() => {
        const s = taskData.stats;
        return [
            { label: 'Total Tasks', value: s?.totalTasks || 0, bg: '#f5f5f5' },
            { label: 'To Do', value: s?.todo || 0, bg: '#e3f2fd' },
            { label: 'In Progress', value: s?.inProgress || 0, bg: '#f3e5f5' },
            { label: 'Completed', value: s?.completed || 0, bg: '#e8f5e9' },
            { label: 'Overdue', value: s?.overdue || 0, bg: '#FFEBEE', color: '#C62828' }
        ];
    }, [taskData.stats]);

    const resetFilters = () => {
        setState(p => ({
            ...p,
            type: 'All',
            priority: 'All',
            status: 'All',
            search: ''
        }));
    };

    const handlePageChange = (event, newPage) => {
        setState(prev => ({ ...prev, page: newPage }));
    };

    const handleRowsPerPageChange = (event) => {
        setState(prev => ({
            ...prev,
            rowsPerPage: parseInt(event.target.value, 10),
            page: 0
        }));
    };

    const TaskMobileCard = ({ task }) => {
        const statusStyles = getStatusColor(task.status);
        const priorityStyles = priorityColors[task.priority] || { bg: '#f8fafc', color: '#64748b' };
        const typeStyles = issueTypeColors[task.work_type] || { bg: '#f1f5f9', color: '#475569' };
        const isOverdue = task.due_date && dayjs(task.due_date).isBefore(dayjs(), 'day') && task.status !== 'Completed';
        const finalStatusStyles = isOverdue ? getStatusColor('Overdue') : statusStyles;

        return (
            <Card sx={{ borderRadius: '14px', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, wordBreak: 'break-word' }}>{task.task_title}</Typography>
                            <Typography variant="caption" color="text.secondary">{task.task_code}</Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                            {task.submit_for_review ?
                                <RateReview sx={{ fontSize: 18, color: '#2b8a3e' }} /> :
                                <PendingActions sx={{ fontSize: 18, color: '#adb5bd' }} />
                            }
                            <IconButton size="small" onClick={() => navigate(`${roleBasePath}/user-task-details?taskId=${task.id}`)}>
                                <Visibility fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
                        <Chip label={task.work_type} size="small" sx={{ bgcolor: typeStyles.bg, color: typeStyles.color, fontWeight: 700, fontSize: '0.65rem', border: `1px solid ${typeStyles.border || '#e2e8f0'}` }} />
                        <Chip label={task.priority} size="small" sx={{ bgcolor: priorityStyles.bg, color: priorityStyles.color, fontWeight: 700, fontSize: '0.65rem', border: `1px solid ${priorityStyles.border || '#e2e8f0'}` }} />
                        <Chip label={isOverdue ? 'Overdue' : task.status} size="small" sx={{ bgcolor: finalStatusStyles.bg, color: finalStatusStyles.color, fontWeight: 700, fontSize: '0.65rem', border: `1px solid ${finalStatusStyles.border || '#e2e8f0'}` }} />
                        <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                            {`${Math.round(task.progress || 0)}% complete`}
                        </Typography>
                    </Stack>

                    <Divider sx={{ my: 1.5 }} />

                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Start: {formatDate(task.start_date)}</Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Due: {formatDate(task.due_date)}</Typography>
                    </Stack>
                </CardContent>
            </Card>
        );
    };

    return (
        <Box>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                sx={{ borderBottom: '1px solid #c3c3c3', px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 0 } }}
            >
                <IconButton
                    onClick={() => navigate(-1)}
                    sx={{ color: '#64748b', borderRadius: '0px', p: { xs: 0, sm: 1 }, '&:hover': { bgcolor: '#e0e3e6' } }}
                >
                    <ArrowBack sx={{ fontSize: 20 }} />
                    <Typography variant="body2" sx={{ ml: 1, fontWeight: 500, color: '#475569' }}>Back</Typography>
                </IconButton>

                <Divider orientation="vertical" flexItem sx={{ mx: 2, height: 24, alignSelf: 'center', background: '#e2e8f0', display: { xs: 'none', sm: 'block' } }} />

                {/* flexGrow: 1 tells this Box to take up all remaining horizontal space */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexGrow: 1, py: { xs: 1, sm: 1 }, flexWrap: 'wrap', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
                        <Typography variant="h5" fontWeight="bold">User Tasks</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Tooltip title="Project Name" arrow>
                                <Chip label={projectTitle || "N/A"} size="small" variant="outlined" sx={{ cursor: 'pointer' }} />
                            </Tooltip>
                            <Chip label={`Origin: ${origin || "N/A"}`} size="small" variant="outlined" />
                        </Stack>
                    </Box>
                </Box>
            </Stack>

            <Grid container spacing={1.5} sx={{ my: 3 }}>
                {statsCards.map((s, i) => (
                    <Grid size={{ xs: 12, sm: 2.4 }} key={i}>
                        <Card sx={{ p: 1.5, textAlign: 'center', bgcolor: s.bg, borderRadius: 2, border: '1px solid rgba(0,0,0,0.05)' }} elevation={0}>
                            <Typography variant="h6" sx={{ color: s.color || 'inherit', fontWeight: 800 }}>{s.value}</Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>{s.label}</Typography>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#f8fafc' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, sm: 10 }}>
                        <Autocomplete
                            freeSolo
                            disableClearable
                            size="small"
                            options={searchSuggestions}
                            value={state.search}
                            onInputChange={(event, newInputValue) => {
                                setState(p => ({ ...p, search: newInputValue, page: 0 }));
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search by task title or project name..."
                                    sx={{ bgcolor: '#fff' }}
                                    InputProps={{
                                        ...params.InputProps,
                                        type: 'search',
                                        startAdornment: (
                                            <>
                                                <Search fontSize="small" sx={{ ml: 1, color: 'gray' }} />
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 2 }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={state.showFilters ? <FilterAltOff /> : <FilterAlt />}
                            onClick={() => setState(p => ({ ...p, showFilters: !p.showFilters }))}
                            sx={{ textTransform: 'none', bgcolor: '#fff', py: 0.85, lineHeight: 1 }}
                        >
                            {state.showFilters ? 'Hide Filters' : 'Show Filters'}
                        </Button>
                    </Grid>

                    {state.showFilters && (
                        <Grid size={{ xs: 12 }}>
                            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                <Grid size={{ xs: 12, sm: 4, md: 3.5 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Work Type</InputLabel>
                                        <Select
                                            label="Work Type"
                                            value={state.type}
                                            onChange={(e) => setState(p => ({ ...p, type: e.target.value, page: 0 }))}
                                            sx={{ bgcolor: '#fff' }}
                                        >
                                            <MenuItem value="All">All</MenuItem>
                                            <MenuItem value="Bug">Bug</MenuItem>
                                            <MenuItem value="Enhancement">Enhancement</MenuItem>
                                            <MenuItem value="Feature">Feature</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 4, md: 3.5 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Priority</InputLabel>
                                        <Select
                                            label="Priority"
                                            value={state.priority}
                                            onChange={(e) => setState(p => ({ ...p, priority: e.target.value, page: 0 }))}
                                            sx={{ bgcolor: '#fff' }}
                                        >
                                            <MenuItem value="All">All</MenuItem>
                                            <MenuItem value="High">High</MenuItem>
                                            <MenuItem value="Medium">Medium</MenuItem>
                                            <MenuItem value="Low">Low</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 4, md: 3.5 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Status</InputLabel>
                                        <Select label="Status" value={state.status} onChange={(e) => setState(p => ({ ...p, status: e.target.value, page: 0 }))} sx={{ bgcolor: '#fff' }} >
                                            <MenuItem value="All">All</MenuItem>
                                            <MenuItem value="Todo">Todo</MenuItem>
                                            <MenuItem value="In Progress">In Progress</MenuItem>
                                            <MenuItem value="Completed">Completed</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid size={{ sm: 12, md: 1.5 }} sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                    <Button fullWidth startIcon={<RestartAlt />} onClick={resetFilters} sx={{ textTransform: 'none', color: 'text.secondary' }} >
                                        Reset
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><Loader /></Box>
            ) : taskData.tasks.length > 0 ? (
                <>
                    <Stack spacing={1.5} sx={{ display: { xs: 'flex', md: 'none' } }}>
                        {taskData.tasks.map((task) => (
                            <TaskMobileCard key={task.id} task={task} />
                        ))}
                    </Stack>

                    <TableContainer component={Paper} elevation={0} sx={{ display: { xs: 'none', md: 'block' }, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Task Details</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Work Type</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Progress</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Timeline</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600 }}>Review</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {taskData.tasks.map((task) => {
                                    const statusStyles = getStatusColor(task.status);
                                    const priorityStyles = priorityColors[task.priority] || { bg: '#f8fafc', color: '#64748b' };
                                    const typeStyles = issueTypeColors[task.work_type] || { bg: '#f1f5f9', color: '#475569' };
                                    const isOverdue = task.due_date && dayjs(task.due_date).isBefore(dayjs(), 'day') && task.status !== 'Completed';
                                    const finalStatusStyles = isOverdue ? getStatusColor('Overdue') : statusStyles;

                                    return (
                                        <TableRow key={task.id} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="700">{task.task_title}</Typography>
                                                <Typography variant="caption" color="textSecondary">{task.task_code}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={task.work_type} size="small" sx={{ bgcolor: typeStyles.bg, color: typeStyles.color, fontWeight: 700, fontSize: '0.65rem', border: `1px solid ${typeStyles.border || '#e2e8f0'}` }} />
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={task.priority} size="small" sx={{ bgcolor: priorityStyles.bg, color: priorityStyles.color, fontWeight: 700, fontSize: '0.65rem', border: `1px solid ${priorityStyles.border || '#e2e8f0'}` }} />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle' }}>
                                                    {/* Background Circle */}
                                                    <CircularProgress
                                                        variant="determinate"
                                                        value={100}
                                                        size={42}
                                                        thickness={4}
                                                        sx={{ color: '#e2e8f0' }}
                                                    />
                                                    {/* Active Progress Circle */}
                                                    <CircularProgress
                                                        variant="determinate"
                                                        value={Math.min(task.progress || 0, 100)}
                                                        size={42}
                                                        thickness={4}
                                                        sx={{
                                                            color: task.progress >= 100 ? '#22c55e' : '#6366f1',
                                                            position: 'absolute',
                                                            left: 0,
                                                            [`& .MuiCircularProgress-circle`]: { strokeLinecap: 'round' },
                                                        }}
                                                    />
                                                    {/* Percentage Text Overlay */}
                                                    <Box
                                                        sx={{
                                                            top: 0, left: 0, bottom: 0, right: 0,
                                                            position: 'absolute',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 800, color: 'text.secondary' }}>
                                                            {`${Math.round(task.progress || 0)}%`}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" display="block">Start Date: {formatDate(task.start_date)}</Typography>
                                                <Typography variant="caption" display="block">Deadline: {formatDate(task.due_date)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={isOverdue ? 'Overdue' : task.status} size="small" sx={{ bgcolor: finalStatusStyles.bg, color: finalStatusStyles.color, fontWeight: 700, fontSize: '0.65rem', border: `1px solid ${finalStatusStyles.border || '#e2e8f0'}` }} />
                                            </TableCell>
                                            <TableCell align="center">
                                                {task.submit_for_review ?
                                                    <RateReview sx={{ fontSize: 18, color: '#2b8a3e' }} /> :
                                                    <PendingActions sx={{ fontSize: 18, color: '#adb5bd' }} />
                                                }
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" onClick={() => navigate(`${roleBasePath}/user-task-details?taskId=${task.id}`)}>
                                                    <Visibility fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={taskData.total}
                        page={state.page}
                        onPageChange={handlePageChange}
                        rowsPerPage={state.rowsPerPage}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={[5, 10, 25]}
                    />
                </>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, bgcolor: '#fff', borderRadius: 2, border: '1px dashed #cbd5e1' }}>
                    <Typography variant="h6" fontWeight="bold">No Tasks Found</Typography>
                    <Typography variant="body2" color="textSecondary">Try adjusting your filters or search keywords.</Typography>
                </Box>
            )}
        </Box>
    );
}

export default UserTasksPage;